import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import {
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs,
} from '@floating-ui/react';
import {
  Button,
  DropdownCore,
  DropdownItem,
  TDropdownItem,
  TDropdownSection,
  TooltipCore,
  useFloatingTooltip,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { capitaliseFirstLetter } from '@payaca/utilities/stringUtilities';
import { FC, useEffect } from 'react';
import useDropdownMenu from './useDropdownMenu';

export type TProps = {
  id: UniqueIdentifier;
  /**
   * Noun for the item being reordered, e.g. "stage". Drives the tooltip
   * ("Drag to reorder {label}") and the button's aria-label ("{Label} options").
   */
  label: string;
  items: TDropdownItem[] | TDropdownSection[];
  /** Extra classes for the handle button, e.g. to lift it above a
   * `stretched-link` row overlay. */
  className?: string;
  /** Called when the dropdown opens or closes, e.g. to keep the surrounding
   * row highlighted while the menu is open. */
  onOpenChange?: (open: boolean) => void;
};

/**
 * A sortable drag handle that doubles as a dropdown trigger: drag it to reorder
 * the item, click it to open the options menu. Render it inside a dnd-kit
 * sortable context, passing the item's `id`.
 */
const DragHandleWithDropdown: FC<TProps> = (props) => {
  const { id, label, items, className, onOpenChange } = props;

  const { attributes, listeners, setActivatorNodeRef, isDragging } =
    useSortable({ id });

  const dropdown = useDropdownMenu({ enabled: !isDragging });

  useEffect(() => {
    onOpenChange?.(dropdown.isOpen);
  }, [dropdown.isOpen, onOpenChange]);
  const tooltip = useFloatingTooltip({
    enabled: !dropdown.isOpen && !isDragging,
  });

  const mergedRef = useMergeRefs([
    setActivatorNodeRef,
    dropdown.refs.setReference,
    tooltip.refs.setReference,
  ]);

  return (
    <>
      <Button
        ref={mergedRef}
        className={clstx('px-0', className)}
        variant="quaternary"
        size="sm"
        aria-label={`${capitaliseFirstLetter(label)} options`}
        {...tooltip.getReferenceProps(
          dropdown.getReferenceProps({
            ...attributes,
            ...listeners,
          })
        )}
      >
        <UntitledIcon name="draggable" />
      </Button>
      {tooltip.isOpen && (
        <FloatingPortal>
          <TooltipCore
            ref={tooltip.refs.setFloating}
            style={tooltip.floatingStyles}
            {...tooltip.getFloatingProps()}
          >
            <div>
              <b>Drag</b> to reorder {label}
            </div>
            <div>
              <b>Click</b> for more options
            </div>
          </TooltipCore>
        </FloatingPortal>
      )}
      {dropdown.isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={dropdown.context} modal={false}>
            <DropdownCore
              ref={dropdown.refs.setFloating}
              style={dropdown.floatingStyles}
              {...dropdown.getFloatingProps()}
              items={items}
              renderItem={(item, index) => (
                <DropdownItem
                  key={index}
                  ref={(node) => {
                    dropdown.listRef.current[index] = node;
                  }}
                  role="menuitem"
                  item={item}
                  tabIndex={dropdown.activeIndex === index ? 0 : -1}
                  data-active={dropdown.activeIndex === index ? '' : undefined}
                  {...dropdown.getItemProps({
                    onClick: (event: any) => {
                      if (item.disabled) return;
                      item.onClick?.(event);
                      dropdown.setIsOpen(false);
                    },
                  })}
                />
              )}
            />
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

export default DragHandleWithDropdown;
